import { union, uniq } from 'lodash';
import Controller from '../../shared/logic/Controller';
import Contact from '../../shared/model/contact/Contact';
import { findByProp } from '../../shared/util/collection';
import Accounts from './Accounts';
import Filters from './Filters';
import Labels from './Labels';

export const className = 'Contacts';
export const dependenciesRunInState = 'contacts';
export const fields = {
  list: [
    'id',
    'idOwner',
    'avatar',
    {
      person: {
        information: ['firstName', 'middleName', 'familyName'],
        contact: ['primaryCode', 'primary', 'email']
      }
    },
    {
      company: {
        information: ['name'],
        contact: ['phoneCode', 'phone', 'email']
      }
    },
    'idLabels',
    'isCompany',
    'isMarketing',
    'isActive',
    'orderByField'
  ],
  names: [
    'id',
    'avatar',
    {
      person: {
        information: ['firstName', 'middleName', 'familyName']
      }
    },
    {
      company: {
        information: ['name']
      }
    },
    'isCompany'
  ]
};

export default class Contacts extends Controller {
  contactListHeaderSort = (order) => {
    const { orderBy, coerceTo, isDescending } = order;
    const query = { orderBy, coerceTo, isDescending: !isDescending };
    this.Storage.set('OrderBy', query);
    this.reset('available', { query });
  };

  contactsListHeaderLabel = () => {
    this.Scope.Event.dialogLabelSelect({
      event: null,
      labels: this.Scope.get('Data.Labels.Collection.Data'),
      data: this.getDataCollectionSelection()
    });
  };

  contactsListHeaderMailChimp = () => {
    this.Scope.update('isVisible.MailChimp', !this.Scope.get('isVisible.MailChimp'));
  };

  contactsListHeaderCampaignMonitor = () => {
    this.Scope.update(
      'isVisible.CampaignMonitor',
      !this.Scope.get('isVisible.CampaignMonitor')
    );
  };

  contactsListHeaderCampaignMonitorEmbed = () => {
    this.Scope.update(
      'isVisible.CampaignMonitorEmbed',
      !this.Scope.get('isVisible.CampaignMonitorEmbed')
    );
  };

  contactsListHeaderArchive = () => {
    this.contactsArchiveConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', this.getDataCollectionSelection());
  };

  contactsListHeaderUnarchive = () => {
    this.contactsUnarchiveConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', this.getDataCollectionSelection());
  };

  contactsListHeaderDelete = () => {
    this.contactsDeleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', this.getDataCollectionSelection());
  };

  contactsListLabel = (c) => {
    this.Scope.Event.dialogLabelSelect({
      event: null,
      labels: this.Scope.get('Data.Labels.Collection.Data'),
      selectedLabels: c.idLabels,
      data: c
    });
  };

  contactLabelsSelected = async (selectedLabels, contact) => {
    if (contact instanceof Contact) {
      await this.Data.update({ id: contact.id, idLabels: selectedLabels });
    }
    if (Array.isArray(contact)) {
      /* eslint-disable no-restricted-syntax */
      /* eslint-disable no-await-in-loop */
      for (const id of contact) {
        const c = findByProp(this.getDataCollectionData(), id);
        await this.Data.update({ id, idLabels: uniq(union(c.idLabels, selectedLabels)) });
      }
      /* eslint-enable no-restricted-syntax */
      /* eslint-enable no-await-in-loop */
    }
    this.reset();
  };

  contactsListEdit = (c) =>
    this.Scope.State.transitionTo('contactEdit', {
      type: c.getEntity()?.toLowerCase(),
      id: c.id
    });

  contactDuplicate = async (contact) => {
    await this.Data.duplicate({ id: contact.id });
    this.reset();
  };

  contactsConfirmationModalToggle = () => {
    this.Scope.update(
      'isVisible.ConfirmationModal',
      !this.Scope.get('isVisible.ConfirmationModal')
    );
  };

  contactsListConfirm = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'archive' || response.name === 'active') {
      this.contactsListToggleArchiveConfirm(response.value);
    }
    if (response.name === 'delete') {
      this.contactsListDeleteConfirm();
    }
  };

  contactsListCancel = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'archive' || response.name === 'active') {
      this.contactsListToggleArchiveCancel();
    }
    if (response.name === 'delete') {
      this.contactsListDeleteCancel();
    }
  };

  contactsArchiveConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Archiving contacts(s)' : '',
          Text: isConfirm ? 'Are you sure you want to proceed?' : '',
          Response: {
            name: isConfirm ? 'archive' : '',
            value: false
          }
        }
      }
    });
    this.contactsConfirmationModalToggle();
  };

  contactsListArchive = (contact) => {
    this.contactsArchiveConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', [contact.id]);
  };

  contactsUnarchiveConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Unarchiving contacts(s)' : '',
          Text: isConfirm ? 'Are you sure you want to proceed?' : '',
          Response: {
            name: isConfirm ? 'archive' : '',
            value: isConfirm || false
          }
        }
      }
    });
    this.contactsConfirmationModalToggle();
  };

  contactsListUnarchive = (contact) => {
    this.contactsUnarchiveConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', [contact.id]);
  };

  contactsListToggleArchiveConfirm = async (value) => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'archive') {
      this.contactsArchiveConfirmationModalToggle(false);
    }
    if (response.name === 'active') {
      this.contactsUnarchiveConfirmationModalToggle(false);
    }
    await this.Data.request('changeVisibility', null, {
      idContacts: this.Scope.get('Selection.Contacts'),
      isActive: value
    });
    this.reset();
  };

  contactsListToggleArchiveCancel = () => {
    const response = this.Scope.get('Resources.Confirmation.Response');
    if (response.name === 'archive') {
      this.contactsArchiveConfirmationModalToggle(false);
    }
    if (response.name === 'active') {
      this.contactsUnarchiveConfirmationModalToggle(false);
    }
    this.Scope.update('Selection.Contacts', []);
  };

  contactsDeleteConfirmationModalToggle = (isConfirm) => {
    this.Scope.set({
      Resources: {
        Confirmation: {
          Type: isConfirm ? 'error' : '',
          Icon: 'icon-alert-circle',
          Title: isConfirm ? 'Deleting contact(s)' : '',
          Text: isConfirm
            ? 'This will permanently erase all of the contact(s) data, as well as all of the connected data. Data erasure is permanent and not recoverable. Are you sure you want to proceed?'
            : '',
          Response: {
            name: isConfirm ? 'delete' : '',
            value: isConfirm || false
          }
        }
      }
    });
    this.contactsConfirmationModalToggle();
  };

  contactsListDelete = (contact) => {
    this.contactsDeleteConfirmationModalToggle(true);
    this.Scope.update('Selection.Contacts', [contact.id]);
  };

  contactsListDeleteConfirm = async () => {
    this.contactsDeleteConfirmationModalToggle(false);
    await this.Data.request('contactsDelete', null, {
      idContacts: this.Scope.get('Selection.Contacts')
    });
    this.reset();
  };

  contactsListDeleteCancel = () => {
    this.contactsDeleteConfirmationModalToggle(false);
    this.Scope.update('Selection.Contacts', []);
  };

  contactsFiltersToggle = () => {
    const isVisible = this.Scope.get('isVisible.Filters');
    if (!isVisible) {
      this.Scope.Event.intercomHide();
    } else {
      this.Scope.Event.intercomShow();
    }
    this.Scope.update('isVisible.Filters', !isVisible);
  };

  contactAdd = (c) =>
    this.Scope.State.transitionTo('contactEdit', {
      type: c.type?.toLowerCase(),
      id: c.id
    });

  contactView = (c) =>
    this.Scope.State.transitionTo('contactView', {
      type: c.getEntity()?.toLowerCase(),
      id: c.id
    });

  contactsFiltersRemove = () => {
    this.Storage.remove('Filter');
    this.reset('available', { query: { idFilter: '', filterName: '' } });
  };

  contactsFiltersApply = (id, name) => {
    this.contactsFiltersToggle();
    const query = { idFilter: id, filterName: name };
    this.Storage.set('Filter', query);
    this.reset('available', { query });
  };

  contactsStartingLetter = (letter) => {
    const newLetter = letter !== 'ALL' ? letter : '';
    this.Storage.set('StartingWith', newLetter);
    this.reset('available', { query: { startingWith: newLetter } });
  };

  run = async (dependencies, dependenciesForAngular) => {
    const isInRunnableState = await this.init(
      [
        { name: 'contactsListHeaderSort:click', handler: this.contactListHeaderSort },
        {
          name: 'contactsListHeaderLabel:click',
          handler: this.contactsListHeaderLabel
        },
        {
          name: 'contactsListHeaderMailChimp:click',
          handler: this.contactsListHeaderMailChimp
        },
        {
          name: 'contactsListHeaderCampaignMonitor:click',
          handler: this.contactsListHeaderCampaignMonitor
        },
        {
          name: 'contactsListHeaderCampaignMonitorEmbed:click',
          handler: this.contactsListHeaderCampaignMonitorEmbed
        },
        { name: 'contactsListHeaderArchive:click', handler: this.contactsListHeaderArchive },
        {
          name: 'contactsListHeaderUnarchive:click',
          handler: this.contactsListHeaderUnarchive
        },
        { name: 'contactsListHeaderDelete:click', handler: this.contactsListHeaderDelete },

        { name: 'contactsListLabel:click', handler: this.contactsListLabel },
        { name: 'contactsListEdit:click', handler: this.contactsListEdit },
        { name: 'contactsListDuplicate:click', handler: this.contactDuplicate },
        { name: 'contactsListArchive:click', handler: this.contactsListArchive },
        { name: 'contactsListUnarchive:click', handler: this.contactsListUnarchive },
        { name: 'contactsListDelete:click', handler: this.contactsListDelete },

        { name: 'contactsListConfirm:click', handler: this.contactsListConfirm },
        { name: 'contactsListCancel:click', handler: this.contactsListCancel },

        { name: 'contactsFiltersToggle:click', handler: this.contactsFiltersToggle },
        { name: 'contactAddNewPerson:click', handler: this.contactAdd },
        { name: 'contactAddNewCompany:click', handler: this.contactAdd },

        { name: 'itemListClickItem:click', handler: this.contactView },
        { name: 'itemListSelectItem', handler: undefined },

        { name: 'removeFilter:click', handler: this.contactsFiltersRemove },
        { name: 'startingLetter:click', handler: this.contactsStartingLetter }
      ],
      dependencies,
      dependenciesForAngular,
      dependenciesRunInState
    );
    if (!isInRunnableState) {
      return;
    }

    this.Scope.WebPage.pageTitle('Contacts');
    await Promise.all([
      this.Scope.get('Data.Accounts')?.load('available'),
      this.Scope.get('Data.Filters')?.load('available', { query: { type: 'contact' } }),
      this.Scope.get('Data.Labels')?.load('available', { query: { type: 'contact' } })
    ]).then(() => {
      this.Scope.set(
        {
          isReady: { Contacts: true, Accounts: true, Filters: true, Labels: true },
          contactLabelsSelected: this.contactLabelsSelected,
          contactListApplyFilter: this.contactsFiltersApply,
          contactListToggleFilters: this.contactsFiltersToggle,
          contactsListToggleMailChimp: this.contactsListHeaderMailChimp,
          contactsListToggleCampaignMonitor: this.contactsListHeaderCampaignMonitor,
          contactsListToggleCampaignMonitorEmbed: this.contactsListHeaderCampaignMonitorEmbed
        },
        true
      );
    });
  };

  constructor(
    $rootScope,
    $scope,
    $state,
    $timeout,
    webpage,
    eventService,
    authenticationService
  ) {
    'ngInject';

    const angularDependencies = [
      $rootScope,
      $scope,
      $state,
      $timeout,
      webpage,
      eventService,
      authenticationService
    ];
    super(angularDependencies, {
      Storage: {
        Table: className
      },
      Scope: {
        Defaults: {
          Name: className,
          Resources: {
            Confirmation: {
              Width: '50',
              Icon: '',
              Type: '',
              Title: '',
              Text: '',
              Response: {
                name: '',
                value: false
              }
            }
          },
          Selection: { Contacts: [] },
          isVisible: {
            Filters: false,
            MailChimp: false,
            CampaignMonitor: false,
            CampaignMonitorEmbed: false,
            ConfirmationModal: false
          }
        }
      },
      Data: {
        Model: Contact,
        ModelName: className,
        Requests: {
          available: {
            url: '/api/v1/contacts/available/',
            query: {
              fields: JSON.stringify(fields.list),
              orderBy: 'name',
              coerceTo: 'string',
              isDescending: false
            }
          },
          specific: {
            url: '/api/v1/contacts/specific/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            },
            query: {
              idContacts: [],
              fields: JSON.stringify(fields.list),
              orderBy: 'name',
              coerceTo: 'string',
              isDescending: false
            },
            useQueryAsPayload: true
          },
          crud: {
            url: '/api/v1/contact/',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          },
          contactsDelete: {
            url: '/api/v1/contacts/delete/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          },
          changeVisibility: {
            url: '/api/v1/contacts/change-visibility/',
            method: 'POST',
            header: {
              Authorization: `Bearer ${authenticationService.tokenGet()}`
            }
          }
        }
      }
    });

    this.reset = (request, requestParameters, scope, isUpdated) => {
      this.resetDataAndScope(
        request || 'available',
        requestParameters || {},
        scope || {
          Data: { Contacts: this.Data }
        },
        isUpdated || true
      );
    };

    this.run([Accounts, Filters, Labels], angularDependencies);
  }
}
